import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/** Query params to have an order priority. */
const PRIORITY_KEYS = ["page", "limit"];

type QueryParams = {
  [query: string]: string;
};

type APIFilters = {
  [filter: string]: string | number | boolean | undefined;
};

/** Hook for managing query params. */
export default function useQueryParams() {
  const navigate = useNavigate();
  const location = useLocation();

  /** Returns the current query params. If there's none, returns an empty object (`{}`). */
  const queryParams = useMemo(() => {
    if (!location.search) return {};

    const urlSearchParams = new URLSearchParams(location.search);
    const params = Object.fromEntries(urlSearchParams.entries());

    return params;
  }, [location.search]);

  /** Merge the current query params with the new ones. If any property is set `undefined` or is a empty string (`""`), it will be removed. */
  const changeQueryParams = useCallback(
    (newQueryParams: QueryParams | APIFilters) => {
      const resultQueryParam = Object.entries({
        ...queryParams,
        ...newQueryParams
      })
        // Filter out empty values.
        .filter(
          (pair): pair is [string, string | number | boolean] =>
            typeof pair[1] !== "undefined" && pair[1] !== ""
        )
        // Change all values to string.
        .map(([key, value]) => [key, value.toString()])
        // Then, sort by key using localeCompare.
        .sort((a, b) => a[0].localeCompare(b[0], "en"));

      // We put our PRIORITY_KEYS at the beginning of the array.
      // Reversing the array ensure that the PRIORITY_KEYS are in order.
      const reversedPriorityKeys = PRIORITY_KEYS.slice().reverse();

      for (const priorityKey of reversedPriorityKeys) {
        const queryParamIndex = resultQueryParam.findIndex(
          ([key]) => key === priorityKey
        );

        if (queryParamIndex > -1) {
          // Remove the priority key from the array.
          const removedPriorityKey = resultQueryParam.splice(
            queryParamIndex,
            1
          )[0];
          // And put it at the beginning of the array.
          resultQueryParam.unshift(removedPriorityKey);
        }
      }

      const stringifiedUrlSearchParams = new URLSearchParams(
        resultQueryParam
      ).toString();

      navigate({ search: stringifiedUrlSearchParams });
    },
    [navigate, queryParams]
  );

  /** Reset the query params. */
  const resetQueryParams = useCallback(() => {
    navigate({ search: "" });
  }, [navigate]);

  return {
    queryParams,
    changeQueryParams,
    resetQueryParams
  };
}
